import { notifyOnError } from '~/rage/decorators';
import {
	Gender,
	IPedAppearanceValue,
	IPedFaceMorphValue,
	IPedOverlayValue,
	IPedParentsValue,
	PedHeadPosition,
} from '~/shared';
import { GameConfigService } from '../GameConfigService';
import { RpcService } from '../RpcService';
import { AbstractCharacterCreationService } from './AbstractCharacterCreationService';

export class CharacterCreationService extends AbstractCharacterCreationService {
	constructor(
		private readonly rpc: RpcService,
		private readonly gameConfig: GameConfigService,
	) {
		super();

		this.gameConfig.getConfigs().subscribe(() => {
			this.fetchParents();
			this.fetchFaceMorphs();
			this.fetchOverlaySections();
			this.fetchOverlays();
			this.fetchHairColors();
			this.fetchMakeupColors();
		});
	}

	public async setGenderValue(gender: Gender): Promise<void> {
		return await this.rpc.invokeClient('characterCreation:setGender', {
			gender,
		});
	}

	private fetchParents(): void {
		const parents = this.gameConfig.configs?.appearance?.parents;
		if (parents) {
			this.parents.next(parents);
		}
	}

	public async setParentsValue(parents: IPedParentsValue): Promise<void> {
		return await this.rpc.invokeClient('characterCreation:setParents', {
			parents,
		});
	}

	private fetchFaceMorphs(): void {
		const faceMorphs = this.gameConfig.configs?.appearance?.faceMorphs;
		if (faceMorphs) {
			this.faceMorphs.next(faceMorphs);
		}
	}

	public async setFaceMorphValues(morphs: IPedFaceMorphValue[]): Promise<void> {
		return await this.rpc.invokeClient('characterCreation:setFaceMorphs', {
			morphs,
		});
	}

	private fetchOverlaySections(): void {
		const overlaySections =
			this.gameConfig.configs?.appearance?.overlaySections;
		if (overlaySections) {
			this.overlaySections.next(overlaySections);
		}
	}

	private fetchOverlays(): void {
		if (!this.gameConfig.configs) return;

		const overlays =
			this.gameConfig.configs.appearance?.overlays?.filter(
				(o) => o.isEnabled && o.isCharCreationEnabled,
			) ?? [];

		const overlaysFemale = overlays.filter((e) => e.gender !== Gender.MALE);
		this.overlaysFemale.next(overlaysFemale);

		const overlaysMale = overlays.filter((e) => e.gender !== Gender.FEMALE);
		this.overlaysMale.next(overlaysMale);
	}

	public async setOverlayValues(overlays: IPedOverlayValue[]): Promise<void> {
		return await this.rpc.invokeClient('characterCreation:setOverlays', {
			overlays,
		});
	}

	private fetchHairColors(): void {
		if (!this.gameConfig.configs) return;

		const hairColors =
			this.gameConfig.configs.appearance?.hairColors?.filter(
				(o) => o.isEnabled && o.isCharCreationEnabled,
			) ?? [];
		this.hairColors.next(hairColors);
	}

	private fetchMakeupColors(): void {
		if (!this.gameConfig.configs) return;

		const makeupColors =
			this.gameConfig.configs.appearance?.makeupColors?.filter(
				(o) => o.isEnabled && o.isCharCreationEnabled,
			) ?? [];
		this.makeupColors.next(makeupColors);
	}

	public async setAppearanceValue(
		appearance: IPedAppearanceValue,
	): Promise<void> {
		return await this.rpc.invokeClient('characterCreation:setAppearance', {
			appearance,
		});
	}

	public async setHeadPosition(position: PedHeadPosition): Promise<void> {
		return await this.rpc.invokeClient('characterCreation:setHeadPosition', {
			position,
		});
	}

	@notifyOnError
	public async createCharacter(
		firstName: string,
		lastName: string,
		appearance: IPedAppearanceValue,
	): Promise<void> {
		await this.rpc.invokeClient('characterCreation:create', {
			firstName,
			lastName,
			appearance,
		});
	}

	public async exit(): Promise<void> {
		return await this.rpc.invokeClient('characterCreation:close');
	}
}
